import type { BBStates, StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import type { DesignDefinition } from './types';
import { getDesignPresetTokenIds, isDesignPresetTokenCompatible, normalizePlatformStyleValue } from './presets';

export type DesignPresetStyles = Record<string, Record<string, Record<string, unknown>>>;
export type StyleAssignmentLayer = { owner: 'element' | 'master' | 'instance'; styles: DesignPresetStyles };
export type StyleAssignmentSource =
	| { kind: 'local'; owner: StyleAssignmentLayer['owner'] }
	| { kind: 'preset'; presetId: string; presetName: string; authoredPlatform: 'Base' | StoryDefaultPlatform };
export type StyleAssignmentSources = Record<string, Record<string, Record<string, StyleAssignmentSource>>>;
export type PresetDiagnostic = {
	code: 'missing-preset' | 'unsupported-platform' | 'missing-token' | 'incompatible-token';
	presetId: string;
	property?: string;
	platform?: string;
	tokenId?: string;
};
export type EffectiveStyleAssignments = {
	styles: DesignPresetStyles;
	sources: StyleAssignmentSources;
	diagnostics: PresetDiagnostic[];
};

/** Collect authored assignments only; state/platform inheritance remains the reader's job. */
export function collectEffectiveStyleAssignments({
	design,
	presetId,
	mediaQuery,
	layers,
}: {
	design?: DesignDefinition;
	presetId?: string;
	mediaQuery: StoryMediaQuery;
	layers: readonly StyleAssignmentLayer[];
}): EffectiveStyleAssignments {
	const styles: DesignPresetStyles = {};
	const sources: StyleAssignmentSources = {};
	const diagnostics: PresetDiagnostic[] = [];
	const put = (state: string, platform: string, property: string, value: unknown, source: StyleAssignmentSource) => {
		if (value === undefined || value === null || value === '') return;
		((styles[state] ??= {})[platform] ??= {})[property] = value;
		((sources[state] ??= {})[platform] ??= {})[property] = source;
	};
	const preset = design?.presets?.find(item => item.id === presetId);
	if (presetId && !preset) diagnostics.push({ code: 'missing-preset', presetId });
	if (preset) {
		const tokens = new Map(
			design?.tokens.collections.flatMap(collection => collection.tokens.map(token => [token.id, token] as const))
		);
		for (const [property, assignment] of Object.entries(preset.properties)) {
			const assignments = {
				...(assignment.base !== undefined ? { [mediaQuery.defaultPlatform]: assignment.base } : {}),
				...assignment.platforms,
			};
			for (const [platform, value] of Object.entries(assignments)) {
				if (!mediaQuery.config[platform]) {
					diagnostics.push({ code: 'unsupported-platform', presetId: preset.id, property, platform });
					continue;
				}
				for (const tokenId of getDesignPresetTokenIds(value)) {
					const token = tokens.get(tokenId);
					if (!token)
						diagnostics.push({ code: 'missing-token', presetId: preset.id, property, platform, tokenId });
					else if (!isDesignPresetTokenCompatible(property, token.type))
						diagnostics.push({
							code: 'incompatible-token',
							presetId: preset.id,
							property,
							platform,
							tokenId,
						});
				}
				put(
					'defaultState',
					platform,
					property,
					normalizePlatformStyleValue(property, value, mediaQuery, platform as StoryDefaultPlatform),
					{
						kind: 'preset',
						presetId: preset.id,
						presetName: preset.name,
						authoredPlatform:
							assignment.platforms?.[platform] !== undefined
								? (platform as StoryDefaultPlatform)
								: 'Base',
					}
				);
			}
		}
	}
	for (const layer of layers) {
		for (const [state, platforms] of Object.entries(layer.styles)) {
			for (const [platform, values] of Object.entries(platforms)) {
				// Preserve authored empty slots too; a collector never expands absent platforms.
				(styles[state] ??= {})[platform] ??= {};
				(sources[state] ??= {})[platform] ??= {};
				for (const [property, value] of Object.entries(values))
					put(state, platform, property, value, { kind: 'local', owner: layer.owner });
			}
		}
	}
	return { styles, sources, diagnostics };
}

export function resetLocalStyleAssignment({
	styles,
	property,
	state,
	platform,
}: {
	styles: DesignPresetStyles;
	property: string;
	state: BBStates;
	platform: string;
}): DesignPresetStyles {
	const next = structuredClone(styles);
	if (next[state]?.[platform]) delete next[state][platform][property];
	return next;
}
